import { useCallback, useEffect, useState } from "react";
import { Card, Label, MonoMeta } from "@/design/primitives";
import { cn } from "@/lib/utils";

// ── Composer (Phase 7) ──────────────────────────────────────────────────────────
// Type a task, run it on the Studio. Title + prompt + model, one POST to
// /api/exec/tasks. When the models endpoint is empty or down, we show the
// static hint list and one calm line — no spinner, no modal.

const STATIC_MODELS = ["atlas:latest", "gemma3:12b"];

function useModels() {
  const [models, setModels] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [stale, setStale] = useState(true);
  const [error, setError] = useState(false);

  const fetchModels = useCallback(async () => {
    try {
      const res = await fetch("/api/exec/models");
      if (!res.ok) throw new Error("Board unreachable");
      const body = await res.json();
      const list = Array.isArray(body.models) ? body.models : [];
      setModels(list);
      setUpdatedAt(body.updated_at ?? null);
      setStale(Boolean(body.stale));
      setError(false);
    } catch {
      setModels([]);
      setUpdatedAt(null);
      setStale(true);
      setError(true);
    }
  }, []);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  const displayList = models.length > 0 ? models : STATIC_MODELS;
  const waiting = models.length === 0 || error;

  return { displayList, waiting, updatedAt, stale };
}

export const Composer = () => {
  const { displayList, waiting } = useModels();
  const [title, setTitle] = useState("");
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState(displayList[0] || STATIC_MODELS[0]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // Keep the select in sync when the list arrives late.
  useEffect(() => {
    if (displayList.length > 0 && !displayList.includes(model)) {
      setModel(displayList[0]);
    }
  }, [displayList, model]);

  const canSubmit = title.trim().length > 0 && prompt.trim().length > 0 && !busy;

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!canSubmit) return;
      setBusy(true);
      setDone(false);
      try {
        const res = await fetch("/api/exec/tasks", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: title.trim(),
            category: "work",
            executor: "ollama",
            payload: {
              model,
              prompt: prompt.trim(),
              timeout_s: 600,
            },
          }),
        });
        if (!res.ok) throw new Error("Task creation failed");
        await res.json();
        setDone(true);
        setTitle("");
        setPrompt("");
      } catch {
        // swallow — the UI just stays as-is; Jeff can retry
      } finally {
        setBusy(false);
      }
    },
    [canSubmit, title, prompt, model],
  );

  // F8 (desk review 2026-10-29): the composer starts as ONE calm bar (it used
  // to sit between the runway and the reviews, interrupting triage with
  // "start something new"). Focused or holding text, it expands to the full
  // form; submitted, it collapses back to the bar with the queued line.
  const [open, setOpen] = useState(false);
  const openNow = open || title.trim() !== "" || prompt.trim() !== "" || done;
  const onSubmitWrap = useCallback(
    async (e) => {
      await onSubmit(e);
      setOpen(false);
    },
    [onSubmit],
  );

  return (
    <section data-testid="desk-composer" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Label as="h2">Run a task on the Studio</Label>
        <MonoMeta as="p" data-testid="composer-meta">
          {waiting ? "waiting for worker inventory" : `${displayList.length} models ready`}
        </MonoMeta>
      </div>
      {!openNow ? (
        <button
          type="button"
          data-testid="composer-bar"
          onClick={() => setOpen(true)}
          className="flex items-center gap-3 rounded-card border border-os-border bg-white px-5 py-3 text-left transition-colors hover:border-os-border-strong"
        >
          <span className="font-mono text-[0.71875rem] uppercase tracking-[0.08em] text-os-ink-3">
            Run a task
          </span>
          <span className="min-w-0 flex-1 truncate text-[0.875rem] text-os-ink-4">
            Title + prompt + model, one click — the bar opens here
          </span>
          <span aria-hidden="true" className="text-os-ink-4">
            ⌄
          </span>
        </button>
      ) : (
      <Card className="px-5 py-4">
        <form onSubmit={onSubmitWrap} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="composer-title"
              className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-3"
            >
              Title
            </label>
            <input
              id="composer-title"
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (done) setDone(false);
              }}
              placeholder="Short name for the task"
              data-testid="composer-title-input"
              className="w-full rounded-control border border-os-border bg-white px-3 py-2 text-[0.875rem] text-os-ink outline-none placeholder:text-os-ink-4 focus:border-os-border-strong"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="composer-prompt"
              className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-3"
            >
              Prompt
            </label>
            <textarea
              id="composer-prompt"
              rows={4}
              value={prompt}
              onChange={(e) => {
                setPrompt(e.target.value);
                if (done) setDone(false);
              }}
              placeholder="What you want the model to do"
              data-testid="composer-prompt-input"
              className="w-full resize-y rounded-control border border-os-border bg-white px-3 py-2 text-[0.875rem] leading-relaxed text-os-ink outline-none placeholder:text-os-ink-4 focus:border-os-border-strong"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="composer-model"
              className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-3"
            >
              Model
            </label>
            <select
              id="composer-model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              data-testid="composer-model-select"
              className="w-full rounded-control border border-os-border bg-white px-3 py-2 text-[0.875rem] text-os-ink outline-none focus:border-os-border-strong"
            >
              {displayList.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="composer-submit"
              className={cn(
                "rounded-control border px-4 py-2 font-mono text-[0.75rem] transition-colors disabled:opacity-40",
                canSubmit
                  ? "border-os-border-strong bg-os-ink text-white hover:opacity-90"
                  : "border-os-border bg-os-wash text-os-ink-3",
              )}
            >
              {busy ? "Sending…" : "Run locally"}
            </button>
            {done && (
              <p
                data-testid="composer-done"
                className="text-[0.875rem] text-os-green"
              >
                Queued — it will appear in review when done
              </p>
            )}
            <button
              type="button"
              data-testid="composer-collapse"
              onClick={() => setOpen(false)}
              className="ml-auto font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-os-ink-4 underline underline-offset-4 hover:text-os-ink"
            >
              Collapse
            </button>
          </div>
        </form>
      </Card>
      )}
    </section>
  );
};

export default Composer;
